import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import type { LiveTranscript } from '@karehero/models';

import {
  liveTranscriptionSocket,
  useCreateLiveTranscriptionTicketMutation,
} from 'store/api/inference';
import { startPcmCapture, type PcmCapture } from 'utils/pcmCapture';
import { createLiveTranscription } from 'utils/liveTranscriptionSession';

export interface UseLiveTranscriptOptions {
  isEnabled: boolean;
  careCircleId?: string;
  /** The microphone while recording; null once it stops. */
  stream: MediaStream | null;
  /**
   * Whether the recording is held: its audio isn't sent meanwhile, but the
   * session stays open, so its words and recording carry on as it resumes.
   */
  isHeld?: boolean;
}

export interface UseLiveTranscriptResult extends LiveTranscript {
  error: string | null;
  /**
   * Where the API keeps the recording's audio, once the session has taken all
   * of it so far; null while it has not, or if any was lost.
   */
  recordingId: string | null;
}

const IDLE: LiveTranscript = { status: 'idle', text: '', interimText: '' };

export const errCaptionsUnavailable =
  'Live captions are unavailable right now.';

const joinWords = (text: string, more: string) =>
  text ? `${text} ${more.trim()}` : more.trim();

// Breadcrumbs mark each stage, so a Sentry event says how far it got.
const breadcrumb = (message: string, data?: Record<string, unknown>) =>
  Sentry.addBreadcrumb({ category: 'live-captions', message, data });

/**
 * useLiveTranscript captions a recording as it happens: when the microphone
 * opens it starts capturing at once, asks the API for a session ticket, and
 * connects the session, which takes up what was captured meanwhile. The
 * captions outlive the stream, so what was heard stays shown until the next
 * recording begins.
 */
export const useLiveTranscript = ({
  isEnabled,
  careCircleId,
  stream,
  isHeld = false,
}: UseLiveTranscriptOptions): UseLiveTranscriptResult => {
  const [transcript, setTranscript] = useState(IDLE);
  // Read as each frame comes, not when the session started.
  const isHeldRef = useRef(isHeld);
  isHeldRef.current = isHeld;
  const [error, setError] = useState<string | null>(null);
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [createTicket] = useCreateLiveTranscriptionTicketMutation();
  // Words may still land after a recording ends, but not after the next one
  // starts; status and errors only count while the recording is live.
  const generationRef = useRef(0);

  useEffect(() => {
    if (!isEnabled || !careCircleId || !stream) return;

    generationRef.current += 1;
    const generation = generationRef.current;
    const isCurrent = () => generationRef.current === generation;
    let isActive = true;
    let capture: PcmCapture | null = null;
    // The recording is the API's to keep only if the session took every frame.
    let ticketRecordingId: string | null = null;
    let isReady = false;
    let isRecordingLost = false;

    setTranscript({ ...IDLE, status: 'starting' });
    setError(null);
    setRecordingId(null);

    const update = (change: (t: LiveTranscript) => LiveTranscript) => {
      if (isCurrent()) setTranscript(change);
    };

    const showRecording = () => {
      if (!isCurrent()) return;
      setRecordingId(isReady && !isRecordingLost ? ticketRecordingId : null);
    };

    const loseRecording = () => {
      isRecordingLost = true;
      showRecording();
    };

    const fail = (err: unknown) => {
      Sentry.captureException(err);
      loseRecording();
      if (isActive) setError(errCaptionsUnavailable);
    };

    const session = createLiveTranscription({
      onReady: () => {
        breadcrumb('session ready');
        isReady = true;
        showRecording();
        if (isActive) update((t) => ({ ...t, status: 'listening' }));
      },
      onInterim: (interimText) => update((t) => ({ ...t, interimText })),
      onFinal: (final) =>
        update((t) => ({
          ...t,
          text: joinWords(t.text, final),
          interimText: '',
        })),
      onError: (message) => fail(new Error(message)),
      onClose: (code, reason) => {
        breadcrumb('session closed', { code, reason });
        // Closed while still recording, however cleanly: what is said after
        // is never sent, so the API's recording is short.
        if (isActive) loseRecording();
      },
      onAudioDropped: () => {
        breadcrumb('audio dropped while connecting');
        loseRecording();
      },
    });

    // Frames held back are skipped, not ended: the audio either side joins up,
    // in the words and in the API's recording alike.
    startPcmCapture(stream, (frame) => {
      if (!isHeldRef.current) session.sendAudio(frame);
    })
      .then((started) => {
        if (isActive) capture = started;
        else started.stop();
      })
      .catch(fail);

    createTicket({ careCircleId })
      .unwrap()
      .then(
        ({ ticket, recordingId: issuedRecordingId }) => {
          if (!isActive) return;
          breadcrumb('ticket issued');
          ticketRecordingId = issuedRecordingId;
          // An unreadable API URL, or a browser refusing the socket, throws
          // here rather than closing it.
          try {
            session.connect(liveTranscriptionSocket(ticket));
          } catch (err) {
            fail(err);
          }
        },
        // A refused ticket is already reported by the API layer.
        () => {
          if (isActive) setError(errCaptionsUnavailable);
        },
      );

    return () => {
      isActive = false;
      capture?.stop();
      session.finish();
      update((t) => ({ ...t, status: 'ended' }));
    };
  }, [isEnabled, careCircleId, stream, createTicket]);

  return { ...transcript, error, recordingId };
};
